<script setup lang="ts" generic="T extends TableData">
import { ArrowDownIcon, ArrowUpIcon } from "lucide-vue-next"
import type { HTMLAttributes } from "vue"
import { type VNodeChild, computed } from "vue"

import {
  Table as UiTable,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import { cn } from "@/lib/utils"
import type { Column, SortDirection, TableData } from "@/types/table"

type SortPayload = {
  sort_by: string
  order: SortDirection
}

type SortField = keyof T | (string & {})
type SortOrder = SortDirection | (string & {})

const DEFAULT_SORT_ORDER: SortOrder = "desc"

const sortField = defineModel<SortField>("sortBy", {
  default: "",
})
const sortOrder = defineModel<SortOrder>("order", {
  default: DEFAULT_SORT_ORDER,
})
const props = withDefaults(
  defineProps<{
    data: T[]
    columns: Array<Column<T>>
    rowKey?: keyof T | ((row: T, index: number) => string | number)
    class?: HTMLAttributes["class"]
    horizontalPaddings?: string
  }>(),
  {
    rowKey: "id" as keyof T,
    horizontalPaddings:
      "[&_th:first-child]:pl-6 [&_th:last-child]:pr-6 [&_td:first-child]:pl-6 [&_td:last-child]:pr-6",
  },
)

const emit = defineEmits<{
  sort: [payload: SortPayload]
}>()

defineSlots<{
  [name: `${string}-header`]: (props: {
    column: Column<T>
    sorted: boolean
    direction: SortOrder
    toggleSort: () => void
  }) => unknown
  [name: `${string}-cell`]: (props: {
    column: Column<T>
    row: T
    value: T[keyof T]
    index: number
  }) => unknown
  empty: () => unknown
}>()

const visibleColumns = computed(() =>
  props.columns.filter((column) => column.visible !== false),
)

const rows = computed(() => props.data)

function toggleSort(column: Column<T>) {
  if (!column.sortable) return

  const columnKey = String(column.key)

  if (String(sortField.value) !== columnKey) {
    sortField.value = columnKey
    sortOrder.value = DEFAULT_SORT_ORDER
  } else {
    sortOrder.value = sortOrder.value === "asc" ? "desc" : "asc"
  }

  emit("sort", {
    sort_by: String(sortField.value),
    order: sortOrder.value as SortDirection,
  })
}

function getCellValue(row: T, key: keyof T) {
  return row[key]
}

function getDefaultCellContent(row: T, column: Column<T>): VNodeChild {
  const value = getCellValue(row, column.key)

  if (column.render) {
    return column.render({
      row,
      value,
      key: column.key,
    })
  }

  return value == null ? "" : String(value)
}

function getColumnName(column: Column<T>) {
  return column.id ?? String(column.key)
}

function getColumnIdentity(column: Column<T>, index: number) {
  return `${getColumnName(column)}-${index}`
}

function getRowKey(row: T, index: number) {
  if (typeof props.rowKey === "function") {
    return props.rowKey(row, index)
  }

  const key = props.rowKey as keyof T
  return (row[key] as string | number | undefined) ?? index
}
</script>

<template>
  <UiTable :class="cn(props.class, props.horizontalPaddings)">
    <TableHeader>
      <TableRow>
        <TableHead
          v-for="(column, columnIndex) in visibleColumns"
          :key="getColumnIdentity(column, columnIndex)"
          :class="
            cn(
              column.center
                ? 'text-center'
                : column.right
                  ? 'text-right'
                  : 'text-left',
              column.sortable && 'cursor-pointer select-none',
              column.thClass,
            )
          "
          @click="toggleSort(column)"
        >
          <div
            class="inline-flex h-full w-full items-center gap-0.5"
            :class="{
              'justify-center': column.center,
              'justify-end': column.right,
            }"
          >
            <component
              class="size-4"
              v-if="column.sortable && String(sortField) === String(column.key)"
              :class="{
                'text-muted': String(sortField) !== String(column.key),
              }"
              :is="sortOrder === 'asc' ? ArrowUpIcon : ArrowDownIcon"
            />
            <slot
              :name="`${getColumnName(column)}-header`"
              :column="column"
              :sorted="String(sortField) === String(column.key)"
              :direction="sortOrder"
              :toggle-sort="() => toggleSort(column)"
            >
              <span>{{ column.header ?? String(column.key) }}</span>
            </slot>
          </div>
        </TableHead>
      </TableRow>
    </TableHeader>

    <TableBody>
      <TableRow v-for="(row, index) in rows" :key="getRowKey(row, index)">
        <TableCell
          v-for="(column, columnIndex) in visibleColumns"
          :key="getColumnIdentity(column, columnIndex)"
          :class="
            cn(
              column.center
                ? 'text-center'
                : column.right
                  ? 'text-right'
                  : 'text-left',
              column.tdClass,
            )
          "
        >
          <slot
            :name="`${getColumnName(column)}-cell`"
            :column="column"
            :row="row"
            :value="getCellValue(row, column.key)"
            :index="index"
          >
            {{ getDefaultCellContent(row, column) }}
          </slot>
        </TableCell>
      </TableRow>

      <TableRow v-if="!rows.length">
        <TableCell
          :colspan="visibleColumns.length || 1"
          class="text-muted-foreground py-8 text-center"
        >
          <slot name="empty">No data</slot>
        </TableCell>
      </TableRow>
    </TableBody>
  </UiTable>
</template>
